import { formatSnakeValue } from 'src/lib/formatters'

type Signer = {
  name?: string | null
  role?: string | null
  status: string
  routingOrder: number
}

type RequestLike = {
  status: string
  signers?: Signer[] | null
}

// A signer in one of these states is not waited for any more. Mirrors
// isTerminalSignerStatus in api/src/lib/signing/statusRules.ts.
const FINISHED_SIGNER_STATUSES = [
  'COMPLETED',
  'DECLINED',
  'SKIPPED',
  'CANCELLED',
]

const inSigningOrder = (signers?: Signer[] | null) =>
  [...(signers ?? [])].sort((a, b) => a.routingOrder - b.routingOrder)

// Signers still to finish, in signing order
const unfinished = (request: RequestLike) =>
  inSigningOrder(request.signers).filter(
    (signer) => !FINISHED_SIGNER_STATUSES.includes(signer.status)
  )

const findNext = (request: RequestLike) => unfinished(request)[0] ?? null

const isInFlight = (status: string) =>
  status === 'SENT' || status === 'IN_PROGRESS'

export const getSignatureStatusLabel = (request: RequestLike): string => {
  switch (request.status) {
    case 'PENDING':
      return 'Sending'
    case 'COMPLETED':
      return 'Completed'
    case 'DECLINED':
      return 'Declined'
    case 'CANCELLED':
      return 'Cancelled'
    case 'FAILED':
      return 'Failed to send'
    case 'SENT':
    case 'IN_PROGRESS': {
      // Who it is waiting on is the next column's job, and when it went out is
      // the Sent column's. With no signers known there is still one to come.
      return unfinished(request).length > 1
        ? 'Awaiting signatures'
        : 'Awaiting signature'
    }
    default:
      return formatSnakeValue(request.status)
  }
}

export const getSignatureStatusVariant = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return 'success'
    case 'DECLINED':
    case 'CANCELLED':
    case 'FAILED':
      return 'error'
    case 'SENT':
    case 'IN_PROGRESS':
      return 'warning'
    default:
      return 'primary'
  }
}

// Who the request is waiting on: the side and the person ("Applicant · Ann
// Driver"), or just the name for an outside signer, who has no side.
export const getNextSignerHint = (request: RequestLike): string | null => {
  if (!isInFlight(request.status)) return null
  const next = findNext(request)
  if (!next?.name) return null
  const side = SIGNER_ROLE_TEXT[next.role ?? '']
  const who = side ? `${side} · ${next.name}` : next.name
  return next.status === 'VIEWED' ? `${who} · Opened` : who
}

const SIGNER_STATUS_TEXT: Record<string, string> = {
  PENDING: 'Not yet sent',
  SENT: 'Waiting',
  VIEWED: 'Opened',
  COMPLETED: 'Signed',
  DECLINED: 'Declined',
  SKIPPED: 'Skipped',
  CANCELLED: 'Cancelled',
}

const SIGNER_ROLE_TEXT: Record<string, string> = {
  APPLICANT: 'Applicant',
  // The same wording as the Send Documents modal's "Company Signer Email"
  EMPLOYEE: 'Company signer',
}

// Every signer in signing order, for the hover text: names and statuses that
// would not fit in a table cell.
export const getSignerDetails = (
  signers?: Signer[] | null
): { name: string; role: string; status: string }[] =>
  inSigningOrder(signers).map((signer) => ({
    name: signer.name ?? '',
    role: SIGNER_ROLE_TEXT[signer.role ?? ''] ?? '',
    status:
      SIGNER_STATUS_TEXT[signer.status] ?? formatSnakeValue(signer.status),
  }))

// "2 of 3 signed", for requests with more than one signer. A skipped signer is
// not one of the people who must sign.
export const getSignedProgress = (signers?: Signer[] | null): string | null => {
  const required = (signers ?? []).filter((s) => s.status !== 'SKIPPED')
  if (required.length < 2) return null
  const signed = required.filter((s) => s.status === 'COMPLETED').length
  return `${signed} of ${required.length} signed`
}

// One line each: these show in a hover, so they must stay short (a test caps
// the length). A test also checks every status the table can show is here.
export const STATUS_LEGEND: { label: string; meaning: string }[] = [
  { label: 'Sending', meaning: 'Being sent.' },
  {
    label: 'Awaiting signature(s)',
    meaning: 'Sent; not everyone has signed.',
  },
  { label: 'Completed', meaning: 'Everyone signed.' },
  { label: 'Declined', meaning: 'A signer declined.' },
  { label: 'Cancelled', meaning: 'Cancelled before it finished.' },
  { label: 'Failed to send', meaning: 'Rejected; nothing was sent.' },
]

// Shown in Waiting on once nobody is left to sign. One place, so the cell and
// its legend always use the same character.
export const NOBODY_LEFT = '-'

// What the Waiting on hover explains: only what could be misread. Names and
// sides speak for themselves. The dash is last and quoted because, unquoted,
// it reads like a typo.
export const WAITING_ON_LEGEND: { label: string; meaning: string }[] = [
  // Not "sent": Anvil reports a later signer as sent from the start, before
  // the signer ahead of them has finished and they have been emailed.
  { label: 'Waiting', meaning: 'Has not signed yet.' },
  { label: 'Opened', meaning: 'Opened, not signed.' },
  { label: `"${NOBODY_LEFT}"`, meaning: 'Nobody left to sign.' },
]
